import { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { Cookie, Settings } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: true,
  });
  const { t } = useLanguage();

  const applyPreferences = useCallback((prefs) => {
    if (!prefs.analytics) {
      // Disable PostHog tracking
      if (window.posthog) {
        window.posthog.opt_out_capturing();
      }
    } else {
      // Enable PostHog tracking
      if (window.posthog) {
        window.posthog.opt_in_capturing();
      }
    }
  }, []);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Apply saved preferences
      const savedPreferences = JSON.parse(consent);
      applyPreferences(savedPreferences);
    }
  }, [applyPreferences]);

  const handleAcceptAll = useCallback(() => {
    const allAccepted = { necessary: true, analytics: true };
    localStorage.setItem("cookieConsent", JSON.stringify(allAccepted));
    applyPreferences(allAccepted);
    setIsVisible(false);
  }, [applyPreferences]);

  const handleRejectNonEssential = useCallback(() => {
    const essentialOnly = { necessary: true, analytics: false };
    localStorage.setItem("cookieConsent", JSON.stringify(essentialOnly));
    applyPreferences(essentialOnly);
    setIsVisible(false);
  }, [applyPreferences]);

  const handleSavePreferences = useCallback(() => {
    localStorage.setItem("cookieConsent", JSON.stringify(preferences));
    applyPreferences(preferences);
    setIsVisible(false);
  }, [preferences, applyPreferences]);

  const toggleAnalytics = useCallback(() => {
    setPreferences(prev => ({ ...prev, analytics: !prev.analytics }));
  }, []);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center p-4 pointer-events-none">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-2xl w-full pointer-events-auto animate-in slide-in-from-bottom-4 duration-500">
        {/* Main Banner */}
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#1E3A8A]/10 flex items-center justify-center shrink-0">
              <Cookie className="w-6 h-6 text-[#1E3A8A]" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-[#0F172A] mb-2">{t('cookieTitle')}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                {t('cookieDescription')}
              </p>
            </div>
          </div>

          {/* Details Panel */}
          {showDetails && (
            <div className="mt-6 p-4 bg-slate-50 rounded-xl space-y-4">
              <h4 className="font-semibold text-[#0F172A]">{t('cookieSettings')}</h4>
              
              {/* Necessary Cookies */}
              <div className="flex items-center justify-between py-2 border-b border-slate-200">
                <div>
                  <p className="font-medium text-[#0F172A]">{t('cookieNecessary')}</p>
                  <p className="text-xs text-slate-500">{t('cookieNecessaryDesc')}</p>
                </div>
                <div className="px-3 py-1 bg-slate-200 rounded-full text-xs text-slate-600 font-medium">
                  {t('cookieAlwaysActive')}
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium text-[#0F172A]">{t('cookieAnalytics')}</p>
                  <p className="text-xs text-slate-500">{t('cookieAnalyticsDesc')}</p>
                </div>
                <button
                  onClick={toggleAnalytics}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    preferences.analytics ? 'bg-[#10B981]' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      preferences.analytics ? 'left-7' : 'left-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleAcceptAll}
              className="flex-1 bg-[#1E3A8A] hover:bg-[#2563EB] text-white rounded-full py-2.5 font-medium"
            >
              {t('cookieAcceptAll')}
            </Button>
            <Button
              onClick={handleRejectNonEssential}
              variant="outline"
              className="flex-1 border-slate-300 text-slate-700 hover:bg-slate-100 rounded-full py-2.5 font-medium"
            >
              {t('cookieRejectNonEssential')}
            </Button>
            {!showDetails ? (
              <Button
                onClick={() => setShowDetails(true)}
                variant="ghost"
                className="flex items-center gap-2 text-slate-600 hover:text-[#1E3A8A] rounded-full py-2.5"
              >
                <Settings className="w-4 h-4" />
                {t('cookieCustomize')}
              </Button>
            ) : (
              <Button
                onClick={handleSavePreferences}
                className="flex-1 bg-[#10B981] hover:bg-[#059669] text-white rounded-full py-2.5 font-medium"
              >
                {t('cookieSavePreferences')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
